import { IStatus } from "./common/types";

export function setCookie(name: string, value: any, days: number) {
  let expires = "";
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    expires = "; expires=" + date.toUTCString();
  }
  document.cookie = name + "=" + (value || "") + expires + "; path=/";
}

export function getCookie(name: string) {
  const nameEQ = name + "=";
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) == 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
}
export function setLocalStorage(
  name: string,
  value: any,
  stringify: boolean = true
) {
  name = "designingwithml_" + name;
  if (stringify) {
    localStorage.setItem(name, JSON.stringify(value));
  } else {
    localStorage.setItem(name, value);
  }
}

export const downloadBase64File = (base64Data: string, fileName: string) => {
  const linkSource = base64Data;
  const downloadLink = document.createElement("a");
  downloadLink.href = linkSource;
  downloadLink.download = fileName;
  downloadLink.click();
  downloadLink.remove();
};

export function getLocalStorage(name: string, stringify: boolean = true): any {
  name = "designingwithml_" + name;
  if (typeof window !== "undefined") {
    const value = localStorage.getItem(name);
    try {
      if (stringify) {
        return JSON.parse(value!);
      } else {
        return value;
      }
    } catch (e) {
      return null;
    }
  } else {
    return null;
  }
}

export function fetchJSON(
  url: string | URL,
  payload: any = {},
  onSuccess: (data: any) => void,
  onError: (error: IStatus) => void
) {
  return fetch(url, payload)
    .then(function (response) {
      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        onError({
          status: false,
          message:
            "Connection error " + response.status + " " + response.statusText,
        });
        return;
      }
      return response.json().then(function (data) {
        onSuccess(data);
      });
    })
    .catch(function (err) {
      console.log("Fetch Error :-S", err);
      onError({ status: false, message: "Connection error " + err });
    });
}

export function fetchBlob(
  url: string | URL,
  payload: any = {},
  onSuccess: (data: any) => void,
  onError: (error: IStatus) => void
) {
  return fetch(url, payload)
    .then(function (response) {
      if (response.status !== 200) {
        console.log(
          "Looks like there was a problem. Status Code: " + response.status
        );
        onError({
          status: false,
          message:
            "Connection error " + response.status + " " + response.statusText,
        });
        return;
      }
      return response.blob().then(function (data) {
        onSuccess(data);
      });
    })
    .catch(function (err) {
      console.log("Fetch Error :-S", err);
      onError({ status: false, message: "Connection error " + err });
    });
}

export function eraseCookie(name: string) {
  document.cookie = name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
}

export function truncateText(text: string, length = 50) {
  if (text.length > length) {
    return text.substring(0, length) + " ...";
  }
  return text;
}

export function rebalanceWeights(
  weights: number[],
  index: number,
  value: number
) {
  // rebalance weights in weights such that weights[i] = value and other indices are adjusted such that they sum up to 1
  const num_items = weights.length - 1;
  // budget is the amount of weight that is not assigned to index
  const budget = 1 - value;
  // the amount of weight that is assigned to each other index rounded to nearest 2 decimal place
  const other_value = Math.round((budget / num_items) * 100) / 100;
  // the new weights
  const new_weights = weights.map((w, i) => {
    if (i == index) {
      return value;
    } else {
      return other_value;
    }
  });
  return new_weights;
}

// Reduce markdown to plain text so previews can be truncated at any point
// without leaving broken syntax (e.g. a link cut mid-URL rendering literally)
export const stripMarkdown = (markdown: string): string => {
  return markdown
    .replace(/```[\s\S]*?```/g, " ") // fenced code blocks
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1") // images -> alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1") // links -> link text
    .replace(/(\*\*|__)(.*?)\1/g, "$2") // bold
    .replace(/(\*|_)(?=\S)(.*?)(?<=\S)\1/g, "$2") // italics
    .replace(/`([^`]*)`/g, "$1") // inline code
    .replace(/^#{1,6}\s+/gm, "") // headings
    .replace(/^\s*[-*+]\s+/gm, "") // bullet markers
    .replace(/\n+/g, " ") // newlines -> spaces
    .replace(/\s{2,}/g, " ")
    .trim();
};

export const truncateDescription = (
  description: string,
  maxLength: number = 100
): string => {
  const plain = stripMarkdown(description);
  if (plain.length <= maxLength) return plain;
  return plain.slice(0, maxLength) + "...";
};

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  };
  // "YYYY-MM-DD" parses as UTC midnight, which renders as the previous day west of UTC
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const date = dateOnly
    ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3])
    : new Date(dateString);
  return date
    .toLocaleDateString("en-US", options)
    .replace(/(\w+)\s(\d+),\s(\d+)/, "$1 $2, $3");
};
